import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  Link,
  Chip,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { get } from '../../services/apiService';

/**
 * Helper function to get quarter date range
 */
const getQuarterDates = (quarter, year) => {
  const startMonth = (quarter - 1) * 3;
  const endMonth = quarter * 3 - 1;
  return {
    start: new Date(year, startMonth, 1),
    end: new Date(year, endMonth + 1, 0) // Last day of the month
  };
};

/**
 * Helper function to get all months in timeline
 */
const getTimelineMonths = (config) => {
  const months = [];
  let currentQuarter = config.startQuarter;
  let currentYear = config.startYear;
  
  const endQuarter = config.endQuarter;
  const endYear = config.endYear;
  
  // Get start and end dates
  const startDate = new Date(currentYear, (currentQuarter - 1) * 3, 1);
  const endQuarterDates = getQuarterDates(endQuarter, endYear);
  const endDate = endQuarterDates.end;
  
  // Generate all months between start and end
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0); // Last day of the month
    
    months.push({
      month,
      year,
      monthName: monthStart.toLocaleDateString('en-US', { month: 'short' }),
      start: monthStart,
      end: monthEnd,
      quarter: Math.ceil(month / 3)
    });
    
    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  
  return months;
};

/**
 * Helper function to get all quarters in timeline (for grouping)
 */
const getTimelineQuarters = (config) => {
  const quarters = [];
  let currentQuarter = config.startQuarter;
  let currentYear = config.startYear;
  
  const endQuarter = config.endQuarter;
  const endYear = config.endYear;
  
  while (currentYear < endYear || (currentYear === endYear && currentQuarter <= endQuarter)) {
    quarters.push({
      quarter: currentQuarter,
      year: currentYear,
      ...getQuarterDates(currentQuarter, currentYear)
    });
    
    currentQuarter++;
    if (currentQuarter > 4) {
      currentQuarter = 1;
      currentYear++;
    }
  }
  
  return quarters;
};

/**
 * Helper function to get state color
 */
const getStateColor = (state) => {
  const stateLower = (state || '').toLowerCase();
  if (stateLower.includes('done') || stateLower.includes('closed') || stateLower.includes('completed')) {
    return '#4caf50'; // Green
  } else if (stateLower.includes('active') || stateLower.includes('in progress')) {
    return '#ff9800'; // Orange
  } else if (stateLower.includes('new')) {
    return '#2196f3'; // Blue
  } else if (stateLower.includes('hold') || stateLower.includes('on hold')) {
    return '#9e9e9e'; // Grey
  } else {
    return '#757575'; // Dark grey
  }
};

/**
 * Calculate bar color based on time elapsed vs completion percentage
 * Rules:
 * - Light Blue: Closed/Done/Completed state OR 100% completion
 * - Green: % time elapsed > % completion + 0-20
 * - Amber: % time elapsed > % completion + 20-50
 * - Red: % time elapsed > % completion + >50
 */
const calculateBarColor = (item) => {
  // Check if item is done/closed/completed
  const stateLower = (item.state || '').toLowerCase();
  const isCompleted = stateLower.includes('done') || stateLower.includes('closed') || stateLower.includes('completed');
  
  // Check if completion is 100%
  const percentCompletion = Math.max(0, Math.min(100, item.completionPercentage || 0));
  const is100Percent = percentCompletion >= 100;
  
  // If item is done/closed/completed OR 100% completion, show light blue
  if (isCompleted || is100Percent) {
    return '#64b5f6'; // Light Blue
  }
  
  // If no target date or completion percentage, use state color
  if (!item.targetDate || item.completionPercentage === undefined || item.completionPercentage === null) {
    return getStateColor(item.state);
  }
  
  const targetDate = new Date(item.targetDate);
  const createdDate = item.createdDate ? new Date(item.createdDate) : new Date();
  const currentDate = new Date();
  
  // Calculate total duration from created to target
  const totalDuration = targetDate - createdDate;
  
  // If total duration is invalid (target before created), use state color
  if (totalDuration <= 0) {
    return getStateColor(item.state);
  }
  
  // Calculate % time elapsed (can be > 100% if target date is in the past)
  const timeElapsed = currentDate - createdDate;
  const percentTimeElapsed = (timeElapsed / totalDuration) * 100;
  
  // Calculate the difference: % time elapsed - % completion
  const difference = percentTimeElapsed - percentCompletion;
  
  // Apply color rules
  if (difference >= 0 && difference <= 20) {
    return '#4caf50'; // Green
  } else if (difference > 20 && difference <= 50) {
    return '#ff9800'; // Amber/Orange
  } else if (difference > 50) {
    return '#f44336'; // Red
  } else {
    // If completion is ahead of schedule (negative difference), show green
    return '#4caf50'; // Green
  }
};

/**
 * Calculate bar position for a work item (epic or feature)
 */
const calculateBarPosition = (item, timelineBounds) => {
  if (!timelineBounds.start || !timelineBounds.duration) return null;
  if (!item.targetDate) return null; // No target date, no bar
  
  const targetDate = new Date(item.targetDate);
  const createdDate = item.createdDate ? new Date(item.createdDate) : timelineBounds.start;
  
  // Use the earlier of createdDate or timeline start as bar start
  const barStart = createdDate < timelineBounds.start ? timelineBounds.start : createdDate;
  const barEnd = targetDate > timelineBounds.end ? timelineBounds.end : targetDate;
  
  // Ensure bar end is after bar start
  if (barEnd <= barStart) {
    return {
      startPos: 0,
      width: 1,
      color: calculateBarColor(item)
    };
  }
  
  // Calculate positions as percentages
  const startPos = ((barStart - timelineBounds.start) / timelineBounds.duration) * 100;
  const endPos = ((barEnd - timelineBounds.start) / timelineBounds.duration) * 100;
  const width = endPos - startPos;
  
  return {
    startPos: Math.max(0, Math.min(100, startPos)),
    width: Math.max(1, Math.min(100 - startPos, width)),
    color: calculateBarColor(item)
  };
};

/**
 * Epic Row Component
 */
const EpicRow = ({ epic, features, timelineBounds, months, quarters, isExpanded, onToggle }) => {
  const adoUrl = `https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${epic.id}`;
  const barData = calculateBarPosition(epic, timelineBounds);
  const EPIC_COLUMN_WIDTH = 200;
  const ROW_HEIGHT = 70; // Increased height to accommodate title above bar
  const FEATURE_ROW_HEIGHT = 60; // Increased height for features
  const BAR_HEIGHT = 28;
  const TITLE_HEIGHT = 20; // Height for title area above bar
  const INDENT_WIDTH = 30;

  return (
    <>
      {/* Epic Row */}
      <Box
        sx={{
          display: 'flex',
          height: `${ROW_HEIGHT}px`,
          minHeight: `${ROW_HEIGHT}px`,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        {/* Epic label column */}
        <Box
          sx={{
            width: `${EPIC_COLUMN_WIDTH}px`,
            flexShrink: 0,
            px: 1.5,
            py: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            borderRight: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper'
          }}
        >
          <IconButton
            size="small"
            onClick={onToggle}
            sx={{ mr: 0.5, p: 0.5 }}
          >
            {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
          <Tooltip title={`${epic.id} - ${epic.title}`} arrow placement="right">
            <Link
              href={adoUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ 
                fontWeight: 'bold', 
                color: 'primary.main',
                textDecoration: 'none',
                fontSize: '0.85rem',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              #{epic.id}
            </Link>
          </Tooltip>
          <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexWrap: 'wrap' }}>
            <Chip
              label={epic.state || 'Unknown'}
              size="small"
              sx={{
                height: '20px',
                fontSize: '0.65rem',
                backgroundColor: barData?.color || '#757575',
                color: 'white',
                '& .MuiChip-label': {
                  px: 0.75,
                  py: 0
                }
              }}
            />
          </Box>
        </Box>

        {/* Gantt bar area */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid',
            borderColor: 'divider',
            pt: 0.5
          }}
        >
          {/* Title above the bar */}
          {barData && barData.width > 5 && (
            <Box
              component={Link}
              href={adoUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                position: 'absolute',
                left: `${barData.startPos}%`,
                width: `${barData.width}%`,
                top: 0,
                height: `${TITLE_HEIGHT}px`,
                zIndex: 4,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                px: 0.5
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'black',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%'
                }}
              >
                {epic.title && epic.title.length > 50 
                  ? `${epic.title.substring(0, 50)}...` 
                  : epic.title}
              </Typography>
            </Box>
          )}
          
          {/* Bar container - positioned below title */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: `${BAR_HEIGHT}px`,
              mt: `${TITLE_HEIGHT}px`
            }}
          >
            {/* Quarter divider lines */}
            {quarters.map((q, index) => {
              if (index === 0) return null;
              const quarterStart = ((q.start - timelineBounds.start) / timelineBounds.duration) * 100;
              return (
                <Box
                  key={`divider-${q.year}-Q${q.quarter}`}
                  sx={{
                    position: 'absolute',
                    left: `${quarterStart}%`,
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    backgroundColor: 'primary.main',
                    zIndex: 1
                  }}
                />
              );
            })}
            
            {/* Month divider lines */}
            {months.map((m, index) => {
              if (index === 0) return null;
              const monthStart = ((m.start - timelineBounds.start) / timelineBounds.duration) * 100;
              return (
                <Box
                  key={`month-divider-${m.year}-${m.month}`}
                  sx={{
                    position: 'absolute',
                    left: `${monthStart}%`,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    backgroundColor: 'divider',
                    zIndex: 1
                  }}
                />
              );
            })}

            {/* Epic Gantt bar with story points progress */}
            {barData && (
            <Tooltip
              title={
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {epic.title}
                  </Typography>
                  <Typography variant="body2">ID: {epic.id}</Typography>
                  <Typography variant="body2">State: {epic.state || 'Unknown'}</Typography>
                  <Typography variant="body2">Team: {epic.team || 'Unknown'}</Typography>
                  <Typography variant="body2">
                    Target Date: {new Date(epic.targetDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2">
                    Progress: {epic.completionPercentage || 0}%
                  </Typography>
                </Box>
              }
              arrow
            >
              <Box
                component={Link}
                href={adoUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  position: 'absolute',
                  left: `${barData.startPos}%`,
                  width: `${barData.width}%`,
                  minWidth: '24px',
                  height: `${BAR_HEIGHT}px`,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  zIndex: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                  textDecoration: 'none',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                  }
                }}
              >
                {/* Background bar (remaining portion) - lighter/transparent */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: barData.color,
                    opacity: 0.25,
                    borderRadius: '4px',
                    zIndex: 2
                  }}
                />
                
                {/* Completed portion - darker/full color */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${Math.max(epic.completionPercentage || 0, 0)}%`,
                    minWidth: (epic.completionPercentage || 0) > 0 ? '2px' : '0px',
                    height: '100%',
                    backgroundColor: barData.color,
                    borderRadius: '4px 0 0 4px',
                    zIndex: 3
                  }}
                />
                
                {/* Percentage label - always show, positioned at the end of progress bar or start if 0% */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${barData.startPos + Math.max((epic.completionPercentage || 0) * barData.width / 100, 0)}%`,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 5,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '3px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    pointerEvents: 'none',
                    border: '1px solid rgba(0,0,0,0.1)',
                    ml: (epic.completionPercentage || 0) === 0 ? '4px' : '2px',
                    whiteSpace: 'nowrap',
                    maxWidth: `${barData.width}%`
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      color: 'black'
                    }}
                  >
                    {epic.completionPercentage !== undefined ? `${epic.completionPercentage}%` : '0%'}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          )}
          </Box>
        </Box>
      </Box>

      {/* Features (collapsible) */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        {features && features.length > 0 ? (
          features.map((feature) => {
          const featureAdoUrl = `https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${feature.id}`;
          // Only calculate bar position if feature has a target date
          const featureBarData = feature.targetDate ? calculateBarPosition(feature, timelineBounds) : null;
          
          return (
            <Box
              key={feature.id}
              sx={{
                display: 'flex',
                height: `${FEATURE_ROW_HEIGHT}px`,
                minHeight: `${FEATURE_ROW_HEIGHT}px`,
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              {/* Feature label column with indent */}
              <Box
                sx={{
                  width: `${EPIC_COLUMN_WIDTH}px`,
                  flexShrink: 0,
                  px: 1.5,
                  py: 0.75,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50',
                  pl: `${INDENT_WIDTH + 24}px` // Indent for hierarchy
                }}
              >
                <Tooltip title={`${feature.id} - ${feature.title}`} arrow placement="right">
                  <Link
                    href={featureAdoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ 
                      fontWeight: 'medium', 
                      color: 'text.secondary',
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    #{feature.id}
                  </Link>
                </Tooltip>
                <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={feature.state || 'Unknown'}
                    size="small"
                    sx={{
                      height: '18px',
                      fontSize: '0.6rem',
                      backgroundColor: featureBarData?.color || '#757575',
                      color: 'white',
                      '& .MuiChip-label': {
                        px: 0.5,
                        py: 0
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Feature Gantt bar area */}
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50',
                  pt: 0.5
                }}
              >
                {/* Feature title above the bar */}
                {featureBarData && featureBarData.width > 5 && (
                  <Box
                    component={Link}
                    href={featureAdoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      position: 'absolute',
                      left: `${featureBarData.startPos}%`,
                      width: `${featureBarData.width}%`,
                      top: 0,
                      height: `${TITLE_HEIGHT}px`,
                      zIndex: 4,
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      px: 0.5
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'black',
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%'
                      }}
                    >
                      {feature.title && feature.title.length > 40 
                        ? `${feature.title.substring(0, 40)}...` 
                        : feature.title}
                    </Typography>
                  </Box>
                )}
                
                {/* Feature bar container - positioned below title */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: `${BAR_HEIGHT - 4}px`,
                    mt: `${TITLE_HEIGHT}px`
                  }}
                >
                {/* Quarter divider lines */}
                {quarters.map((q, index) => {
                  if (index === 0) return null;
                  const quarterStart = ((q.start - timelineBounds.start) / timelineBounds.duration) * 100;
                  return (
                    <Box
                      key={`feature-divider-${feature.id}-${q.year}-Q${q.quarter}`}
                      sx={{
                        position: 'absolute',
                        left: `${quarterStart}%`,
                        top: 0,
                        bottom: 0,
                        width: '2px',
                        backgroundColor: 'primary.main',
                        zIndex: 1
                      }}
                    />
                  );
                })}
                
                {/* Month divider lines */}
                {months.map((m, index) => {
                  if (index === 0) return null;
                  const monthStart = ((m.start - timelineBounds.start) / timelineBounds.duration) * 100;
                  return (
                    <Box
                      key={`feature-month-divider-${feature.id}-${m.year}-${m.month}`}
                      sx={{
                        position: 'absolute',
                        left: `${monthStart}%`,
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        backgroundColor: 'divider',
                        zIndex: 1
                      }}
                    />
                  );
                })}

                  {/* Feature background bar */}
                  {featureBarData && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: `${featureBarData.startPos}%`,
                        width: `${featureBarData.width}%`,
                        height: '100%',
                        backgroundColor: featureBarData.color,
                        opacity: 0.25,
                        borderRadius: '3px',
                        zIndex: 2
                      }}
                    />
                  )}
                  
                  {/* Feature Gantt bar - only show if feature has target date */}
                  {featureBarData && (
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {feature.title}
                          </Typography>
                          <Typography variant="body2">ID: {feature.id}</Typography>
                          <Typography variant="body2">State: {feature.state || 'Unknown'}</Typography>
                          <Typography variant="body2">Team: {feature.team || 'Unknown'}</Typography>
                          {feature.targetDate && (
                            <Typography variant="body2">
                              Target Date: {new Date(feature.targetDate).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                    >
                      <Box
                        component={Link}
                        href={featureAdoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: `${featureBarData.startPos}%`,
                          width: `${featureBarData.width}%`,
                          height: '100%',
                          backgroundColor: featureBarData.color,
                          borderRadius: '3px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          textDecoration: 'none',
                          zIndex: 3,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                          '&:hover': {
                            opacity: 0.9,
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          );
        }))
        : (
          <Box
            sx={{
              display: 'flex',
              height: '40px',
              alignItems: 'center',
              px: 2,
              pl: `${INDENT_WIDTH + 24}px`,
              backgroundColor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
              color: 'text.secondary',
              fontSize: '0.8rem'
            }}
          >
            No features with target dates found for this epic.
          </Box>
        )}
      </Collapse>
    </>
  );
};

/**
 * EpicGanttChart component for displaying epics and features in an expandable tree
 */
const EpicGanttChart = ({ epics = [], timelineConfig = null, onTeamFilterChange }) => {
  const EPIC_COLUMN_WIDTH = 200;
  const HEADER_HEIGHT = 110;
  const [expandedEpics, setExpandedEpics] = useState(new Set());
  const [selectedTeam, setSelectedTeam] = useState('');
  const [enabledTeams, setEnabledTeams] = useState([]);
  const [selectedStream, setSelectedStream] = useState(0);
  const [streams, setStreams] = useState([]);

  // Fetch enabled teams on mount
  useEffect(() => {
    const fetchEnabledTeams = async () => {
      try {
        const response = await get('/api/admin/teams/enabled');
        if (response.success) {
          setEnabledTeams(response.data || []);
          
          // Extract unique streams (normalize case for matching)
          const uniqueStreams = [...new Set(response.data.map(team => team.stream || 'Unassigned').filter(s => s))];
          
          // Custom sort order: Core, Platform & Compliance, CoCo for HighQ, CoCo Txm, AI-Experience, Unassigned, then any others
          // Use case-insensitive matching to handle variations
          const streamOrder = ['Core', 'Platform & Compliance', 'CoCo for HighQ', 'CoCo Txm', 'AI-Experience', 'Unassigned'];
          uniqueStreams.sort((a, b) => {
            // Find index using case-insensitive comparison
            const indexA = streamOrder.findIndex(order => order.toLowerCase() === a.toLowerCase());
            const indexB = streamOrder.findIndex(order => order.toLowerCase() === b.toLowerCase());
            
            // If both are in the order list, sort by their position
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            // If only A is in the order list, A comes first
            if (indexA !== -1) return -1;
            // If only B is in the order list, B comes first
            if (indexB !== -1) return 1;
            // If neither is in the order list, sort alphabetically
            return a.localeCompare(b);
          });
          
          // Debug: Log streams found
          console.log('[Gantt Chart] Available streams:', uniqueStreams);
          const cocoTxmStream = uniqueStreams.find(s => s.toLowerCase().includes('coco txm') || s.toLowerCase().includes('coco tx'));
          if (cocoTxmStream) {
            console.log(`[Gantt Chart] Found CoCo TxM stream: "${cocoTxmStream}"`);
            const cocoTxmTeams = response.data.filter(team => {
              const teamStream = (team.stream || 'Unassigned').trim();
              return teamStream.toLowerCase() === cocoTxmStream.toLowerCase();
            });
            console.log(`[Gantt Chart] Teams in CoCo TxM stream:`, cocoTxmTeams.map(t => ({ id: t.id, name: t.name, enabled: t.enabled, stream: t.stream })));
          } else {
            console.warn('[Gantt Chart] No CoCo TxM stream found. Available streams:', uniqueStreams);
          }
          
          setStreams(uniqueStreams);
        }
      } catch (error) {
        console.error('Error fetching enabled teams:', error);
      }
    };
    
    fetchEnabledTeams();
  }, []);

  // Get enabled team IDs as a Set for quick lookup
  const enabledTeamIds = useMemo(() => {
    return new Set(enabledTeams.map(team => team.id));
  }, [enabledTeams]);

  // Get teams for the selected stream
  const availableTeams = useMemo(() => {
    if (streams.length === 0) return [];
    
    const currentStream = streams[selectedStream] || streams[0];
    const streamTeams = enabledTeams
      .filter(team => (team.stream || 'Unassigned') === currentStream)
      .map(team => team.id)
      .sort();
    
    return streamTeams;
  }, [enabledTeams, streams, selectedStream]);

  // Calculate timeline months first (needed for bounds and rendering)
  const months = useMemo(() => {
    if (!timelineConfig) return [];
    return getTimelineMonths(timelineConfig);
  }, [timelineConfig]);

  // Calculate timeline bounds (needed for filtering)
  const timelineBounds = useMemo(() => {
    if (months.length === 0) return { start: null, end: null, duration: 0 };
    const start = months[0].start;
    const end = months[months.length - 1].end;
    const duration = end - start;
    return { start, end, duration };
  }, [months]);

  // Filter epics based on enabled teams, selected team, stream, and timeline bounds
  const filteredEpics = useMemo(() => {
    let result = epics;
    
    // First, filter by enabled teams only
    if (enabledTeamIds.size > 0) {
      result = result.filter(epic => {
        // Include epic if its team is enabled
        if (epic.team && enabledTeamIds.has(epic.team)) {
          return true;
        }
        // Include epic if any of its features' teams are enabled
        if (epic.features && epic.features.some(feature => feature.team && enabledTeamIds.has(feature.team))) {
          return true;
        }
        return false;
      });
    }
    
    // Filter by stream (if streams are available)
    if (streams.length > 0 && selectedStream < streams.length) {
      const currentStream = streams[selectedStream];
      // Use case-insensitive matching for stream names to handle variations like "CoCo Txm" vs "CoCo TxM"
      const streamTeamIds = new Set(
        enabledTeams
          .filter(team => {
            const teamStream = (team.stream || 'Unassigned').trim();
            return teamStream.toLowerCase() === currentStream.toLowerCase();
          })
          .map(team => team.id)
      );
      
      // Debug logging for stream filtering
      if (currentStream && currentStream.toLowerCase().includes('coco')) {
        console.log(`[Gantt Chart] Filtering by stream: "${currentStream}"`);
        console.log(`[Gantt Chart] Found ${streamTeamIds.size} teams in this stream:`, Array.from(streamTeamIds));
        console.log(`[Gantt Chart] Enabled teams for this stream:`, enabledTeams
          .filter(team => {
            const teamStream = (team.stream || 'Unassigned').trim();
            return teamStream.toLowerCase() === currentStream.toLowerCase();
          })
          .map(team => ({ id: team.id, name: team.name, stream: team.stream }))
        );
      }
      
      if (streamTeamIds.size > 0) {
        const beforeFilter = result.length;
        result = result.filter(epic => {
          // Include epic if its team is in the selected stream
          if (epic.team && streamTeamIds.has(epic.team)) {
            return true;
          }
          // Include epic if any of its features' teams are in the selected stream
          if (epic.features && epic.features.some(feature => feature.team && streamTeamIds.has(feature.team))) {
            return true;
          }
          return false;
        });
        
        // Debug logging
        if (currentStream && currentStream.toLowerCase().includes('coco')) {
          console.log(`[Gantt Chart] Epics before stream filter: ${beforeFilter}, after: ${result.length}`);
          if (result.length === 0 && beforeFilter > 0) {
            console.warn(`[Gantt Chart] No epics found for stream "${currentStream}". Possible reasons:`);
            console.warn(`  - Epics don't have teams assigned that match stream teams`);
            console.warn(`  - Epics don't have target dates (required for Gantt chart)`);
            console.warn(`  - Epics' target dates are outside the timeline range`);
          }
        }
      } else {
        // Debug logging when no teams found for stream
        if (currentStream && currentStream.toLowerCase().includes('coco')) {
          console.warn(`[Gantt Chart] No enabled teams found for stream "${currentStream}"`);
          console.warn(`[Gantt Chart] Available streams from teams:`, [...new Set(enabledTeams.map(t => t.stream).filter(s => s))]);
        }
      }
    }
    
    // Filter by timeline: only include epics whose target date is within or overlaps the timeline
    if (timelineBounds.start && timelineBounds.end) {
      result = result.filter(epic => {
        if (!epic.targetDate) return false; // Epics without target dates are excluded
        
        const targetDate = new Date(epic.targetDate);
        // Include epic if target date is within timeline range
        // Also include if epic has a createdDate that falls within timeline (for epics that span the timeline)
        const createdDate = epic.createdDate ? new Date(epic.createdDate) : null;
        
        // Epic is included if:
        // 1. Target date is within timeline range, OR
        // 2. Created date is within timeline range (epic spans into timeline), OR
        // 3. Epic starts before timeline but ends within timeline
        const targetInRange = targetDate >= timelineBounds.start && targetDate <= timelineBounds.end;
        const createdInRange = createdDate && createdDate >= timelineBounds.start && createdDate <= timelineBounds.end;
        const spansIntoRange = createdDate && createdDate < timelineBounds.start && targetDate >= timelineBounds.start;
        
        return targetInRange || createdInRange || spansIntoRange;
      });
    }
    
    // Filter by team if selected
    if (selectedTeam) {
      result = result.filter(epic => {
        // Check if epic team matches
        if (epic.team === selectedTeam) return true;
        
        // Check if any feature team matches
        if (epic.features && epic.features.some(feature => feature.team === selectedTeam)) {
          return true;
        }
        
        return false;
      }).map(epic => {
        // If epic team doesn't match but feature does, filter features
        if (epic.team !== selectedTeam && epic.features) {
          return {
            ...epic,
            features: epic.features.filter(feature => feature.team === selectedTeam)
          };
        }
        return epic;
      });
    }
    
    return result;
  }, [epics, enabledTeamIds, streams, selectedStream, selectedTeam, timelineBounds, enabledTeams]);

  // Handle team filter change
  const handleTeamChange = (event) => {
    const team = event.target.value;
    setSelectedTeam(team);
    if (onTeamFilterChange) {
      onTeamFilterChange(team);
    }
  };

  // Calculate timeline quarters (for grouping)
  const quarters = useMemo(() => {
    if (!timelineConfig) return [];
    return getTimelineQuarters(timelineConfig);
  }, [timelineConfig]);


  // Toggle epic expansion
  const toggleEpic = (epicId) => {
    setExpandedEpics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(epicId)) {
        newSet.delete(epicId);
      } else {
        newSet.add(epicId);
      }
      return newSet;
    });
  };

  if (!timelineConfig || months.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Loading timeline configuration...
        </Typography>
      </Paper>
    );
  }

  if (epics.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Epic Gantt Chart
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No epics with target dates found.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Epic Gantt Chart
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Team</InputLabel>
          <Select
            value={selectedTeam}
            label="Filter by Team"
            onChange={handleTeamChange}
          >
            <MenuItem value="">
              <em>All Teams</em>
            </MenuItem>
            {availableTeams.map((team) => (
              <MenuItem key={team} value={team}>
                {team}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {/* Stream Tabs */}
      {streams.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={selectedStream} 
            onChange={(e, newValue) => {
              setSelectedStream(newValue);
              setSelectedTeam(''); // Reset team filter when switching streams
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {streams.map((stream, index) => (
              <Tab key={stream} label={stream} value={index} />
            ))}
          </Tabs>
        </Box>
      )}
      
      <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
        <Box sx={{ minWidth: `${EPIC_COLUMN_WIDTH + 600}px`, position: 'relative' }}>
          {/* Header with quarter and month labels */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              backgroundColor: 'background.paper',
              borderBottom: '2px solid',
              borderColor: 'divider',
              mb: 0
            }}
          >
            <Box sx={{ display: 'flex', height: `${HEADER_HEIGHT}px` }}>
              {/* Epic column header */}
              <Box 
                sx={{ 
                  width: `${EPIC_COLUMN_WIDTH}px`,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50'
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  Epic / Feature
                </Typography>
              </Box>
              
              {/* Timeline header - Monthly blocks grouped by quarter */}
              <Box sx={{ flex: 1, position: 'relative', borderTop: '1px solid', borderColor: 'divider' }}>
                {/* Quarter grouping headers */}
                {quarters.map((q, index) => {
                  const quarterStart = ((q.start - timelineBounds.start) / timelineBounds.duration) * 100;
                  const quarterWidth = ((q.end - q.start) / timelineBounds.duration) * 100;
                  
                  return (
                    <Box
                      key={`quarter-${q.year}-Q${q.quarter}`}
                      sx={{
                        position: 'absolute',
                        left: `${quarterStart}%`,
                        width: `${quarterWidth}%`,
                        top: 0,
                        height: '40px',
                        borderLeft: index > 0 ? '2px solid' : 'none',
                        borderRight: index === quarters.length - 1 ? 'none' : '2px solid',
                        borderColor: 'primary.main',
                        px: 1.5,
                        py: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'primary.light',
                        zIndex: 5
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.8rem', color: 'primary.contrastText' }}>
                        Q{q.quarter} {q.year}
                      </Typography>
                    </Box>
                  );
                })}
                
                {/* Monthly blocks */}
                {months.map((m, index) => {
                  const monthStart = ((m.start - timelineBounds.start) / timelineBounds.duration) * 100;
                  const monthWidth = ((m.end - m.start) / timelineBounds.duration) * 100;
                  
                  return (
                    <Box
                      key={`${m.year}-${m.month}`}
                      sx={{
                        position: 'absolute',
                        left: `${monthStart}%`,
                        width: `${monthWidth}%`,
                        top: '40px',
                        bottom: 0,
                        borderLeft: index > 0 ? '1px solid' : 'none',
                        borderRight: index === months.length - 1 ? 'none' : '1px solid',
                        borderColor: 'divider',
                        px: 0.75,
                        py: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        backgroundColor: 'grey.50'
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                        {m.monthName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.25 }}>
                        {m.year}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Epic and Feature rows */}
          <Box>
            {filteredEpics.map((epic) => (
              <EpicRow
                key={epic.id}
                epic={epic}
                features={epic.features || []}
                timelineBounds={timelineBounds}
                months={months}
                quarters={quarters}
                isExpanded={expandedEpics.has(epic.id)}
                onToggle={() => toggleEpic(epic.id)}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default EpicGanttChart;
